// UnsyncedCaptureProvider — mounts the held-work probe machine ONCE for the
// whole /capture subtree and shares its live count via context.
//
// WHY a provider (audit PR #732 / M1+M2). The honest "N changes not yet saved"
// banner must (a) stay visible while the surveyor scrolls a capture screen and
// (b) keep its probe machine (poll + connector subscriptions) alive across
// base → site → building → room navigations. (a) means the banner lives INSIDE
// the sticky CaptureLayout header (as its top row); (b) means the hook must NOT
// be called from CaptureLayout, which remounts per screen — calling it there
// would tear down and rebuild the probe machine on every navigation.
//
// This provider resolves the tension: it is mounted once at the /capture PARENT
// route (router.tsx), which persists across screen navigations, calls
// `useUnsyncedCaptureCount` exactly once, and publishes the count via context.
// CaptureLayout + UnsyncedCaptureBanner READ the context (`useContext`) — they
// never call the hook — so the "hook mounted once" contract holds (lint #6) AND
// the banner can render inside the sticky header without re-instantiating the
// probe per screen.
//
// The context default ({ ready: false, count: null }) renders the banner as
// nothing when read OUTSIDE a provider (a CaptureLayout mounted standalone in a
// test or an isolated screen render): no held-work data is available, so the
// banner correctly shows nothing rather than throwing. The production wiring —
// that the provider really wraps /capture — is pinned by the route test and the
// lint gate, not by a runtime throw here.

import {
  createContext,
  useContext,
  type ReactElement,
  type ReactNode,
} from 'react'

import {
  useUnsyncedCaptureCount,
  type UnsyncedCaptureCount,
} from '@/hooks/powersync/use-unsynced-capture-count'

const DEFAULT_COUNT: UnsyncedCaptureCount = { ready: false, count: null }

const UnsyncedCaptureContext =
  createContext<UnsyncedCaptureCount>(DEFAULT_COUNT)

export function UnsyncedCaptureProvider({
  children,
}: {
  children: ReactNode
}): ReactElement {
  const value = useUnsyncedCaptureCount()
  return (
    <UnsyncedCaptureContext.Provider value={value}>
      {children}
    </UnsyncedCaptureContext.Provider>
  )
}

/** Read the shared held-work count. Returns the inert default outside a provider. */
export function useUnsyncedCaptureContext(): UnsyncedCaptureCount {
  return useContext(UnsyncedCaptureContext)
}
